import {
  ContentWrapper,
  Block,
  Settings,
  SettingSectionTitle,
  SettingsFlexbox,
  MessageWrapper
} from '@/components/DybrComponents/StyledServicePagesElements';
import TextField from '@/components/FormElements/TextField';
import Select from '@/components/FormElements/Select';
import { ActionButton } from '@/components/Shared/Buttons';
import TagsSelect from '@/components/FormElements/TagsSelect';
import { useActiveProfile, useTokenState } from '@/store/localStorage/useTokenState';
import { selectActiveProfileAugmented } from '@/store/user/selectors/selectActiveProfileAugmented';
import { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  applyTagsToImportedApi,
  applyTagsToAllApi,
  deleteEntriesByTagsApi,
  updatePrivacyByTagsApi
} from '@/api/entries';
import { updateTagApi, deleteTagApi, getTagsByProfile } from '@/api/tags';
import { AccessType } from '@/utils/privacy/types/AccessType';
import styled from 'styled-components';

const ActionsRow = styled.div`
  margin-top: ${props => (props.$marginTop ? '10px' : '0')};
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  align-items: center;
  z-index: ${props => props.$zIndex || 'auto'};

  button {
    margin-top: 0 !important;
  }
`;

const SelectWrapper = styled.div`
  width: ${props => props.$width || 'auto'};
`;

const WarningText = styled.p`
  color: var(--red);
`;

const CenteredTextField = styled(TextField)`
  .text-field {
    padding-top: 8px;
    margin-bottom: 0;
  }

  .text-field > div > span:last-child {
    display: none;
  }
`;

const SpacedMessageWrapper = styled(MessageWrapper)`
  margin-top: 20px;
`;

const EMPTY_MESSAGE = { text: '', type: '' };

const INITIAL_MESSAGES = {
  imported: EMPTY_MESSAGE,
  all: EMPTY_MESSAGE,
  privacy: EMPTY_MESSAGE,
  deleteEntries: EMPTY_MESSAGE,
  rename: EMPTY_MESSAGE,
  deleteTag: EMPTY_MESSAGE
};

function SectionMessage({ message }) {
  if (!message.text) return null;
  return (
    <SpacedMessageWrapper type={message.type} show={message.text}>
      {message.text}
    </SpacedMessageWrapper>
  );
}

const STRINGS = {
  TITLE: 'Настройки тегов',
  ERR_DEFAULT: 'что-то пошло не так',
  SUCCESS_IMPORTED: 'Теги успешно добавлены к импортированным записям',
  SUCCESS_ALL: 'Теги успешно добавлены ко всем записям',
  SUCCESS_PRIVACY: 'Приватность записей успешно обновлена',
  SUCCESS_DELETED_ENTRIES: 'Записи успешно удалены',
  SUCCESS_RENAME: 'Тег успешно переименован',
  SUCCESS_DELETE: 'Тег успешно удален',
  CONFIRM_DELETE_ENTRIES:
    'Вы уверены, что хотите удалить все записи с выбранными тегами? Это действие нельзя отменить.',
  CONFIRM_DELETE_TAG: 'Вы уверены, что хотите удалить этот тег из всех записей?',
  SELECT_PLACEHOLDER: 'выберите теги...',
  SELECT_SINGLE_PLACEHOLDER: 'выберите тег...',
  ADD_TAGS_IMPORTED_TITLE: 'Добавить теги к импортированным записям',
  ADD_TAGS_IMPORTED_DESC: 'Выберите теги и примените их ко всем импортированным записям сразу.',
  ADD_TAGS_ALL_TITLE: 'Добавить теги ко всем записям',
  ADD_TAGS_ALL_DESC: 'Выберите теги и примените их ко всем вашим записям сразу.',
  APPLY_IMPORTED: 'Применить',
  APPLY_ALL: 'Применить',
  PRIVACY_TITLE: 'Изменить приватность по тегам',
  PRIVACY_DESC: 'Все записи с указанными тегами получат выбранный уровень приватности.',
  UPDATE_PRIVACY: 'Изменить приватность',
  DELETE_ENTRIES_TITLE: 'Удалить записи по тегам',
  DELETE_ENTRIES_DESC: 'Внимание: будут удалены все записи, содержащие данные теги!',
  DELETE_ENTRIES_BTN: 'Удалить записи',
  RENAME_TITLE: 'Переименовать тег',
  RENAME_DESC: 'Выберите тег из списка и укажите новое название.',
  RENAME_INPUT_LABEL: 'новое имя тега',
  RENAME_BTN: 'Переименовать',
  DELETE_TAG_TITLE: 'Удалить тег',
  DELETE_TAG_DESC: 'Полностью удаляет выбранный тег из всех записей.',
  DELETE_TAG_BTN: 'Удалить тег'
};

const simplePrivacyOptions = [
  { label: 'открыта всем', value: 'public' },
  { label: 'закрыта от незарегистрированных', value: AccessType.registered },
  { label: 'открыта для читателей', value: AccessType.subscribers },
  { label: 'открыта для избранных', value: AccessType.favorites },
  { label: 'закрыта от всех', value: AccessType.private }
];

export function TagsSettingsScene() {
  const dispatch = useDispatch();
  const { activeProfileId } = useActiveProfile();
  const profile = useSelector(state => selectActiveProfileAugmented(state, { activeProfileId }));
  const [token] = useTokenState();

  useEffect(() => {
    document.title = STRINGS.TITLE;
  }, []);

  const [userTags, setUserTags] = useState([]);

  const fetchTags = async () => {
    const res = await getTagsByProfile({ token, profileId: activeProfileId });
    if (!res.error && res.data) {
      setUserTags(res.data.map(t => ({ label: t.name, value: t.id })));
    }
  };

  useEffect(() => {
    fetchTags();
  }, [token]);

  const [messages, setMessages] = useState(INITIAL_MESSAGES);
  const setMessage = (section, text, type) => setMessages(prev => ({ ...prev, [section]: { text, type } }));
  const clearMessages = () => setMessages(INITIAL_MESSAGES);

  const [addTagsImported, setAddTagsImported] = useState([]);
  const [addTagsAll, setAddTagsAll] = useState([]);
  const [privacyTags, setPrivacyTags] = useState([]);
  const [selectedPrivacy, setSelectedPrivacy] = useState(0);
  const [deleteTags, setDeleteTags] = useState([]);

  const userTagsNames = userTags.map(t => ({ label: '#' + t.label, value: t.label }));

  const [renameTagOld, setRenameTagOld] = useState(null);
  const [renameTagNewName, setRenameTagNewName] = useState('');
  const [deleteTag, setDeleteTag] = useState(null);

  const handleApplyToImported = async () => {
    if (addTagsImported.length === 0) return;
    clearMessages();
    const res = await applyTagsToImportedApi({ profileId: activeProfileId, tags: addTagsImported, token });
    if (res.error) setMessage('imported', res.error, 'error');
    else {
      setMessage('imported', STRINGS.SUCCESS_IMPORTED, 'success');
      setAddTagsImported([]);
      await fetchTags();
    }
  };

  const handleApplyToAll = async () => {
    if (addTagsAll.length === 0) return;
    clearMessages();
    const res = await applyTagsToAllApi({ profileId: activeProfileId, tags: addTagsAll, token });
    if (res.error) setMessage('all', res.error, 'error');
    else {
      setMessage('all', STRINGS.SUCCESS_ALL, 'success');
      setAddTagsAll([]);
      await fetchTags();
    }
  };

  const handleUpdatePrivacy = async () => {
    if (privacyTags.length === 0) return;
    clearMessages();
    const privacyType = simplePrivacyOptions[selectedPrivacy].value;
    const res = await updatePrivacyByTagsApi({
      profileId: activeProfileId,
      tags: privacyTags,
      privacy: privacyType,
      token
    });
    if (res.error) setMessage('privacy', res.error, 'error');
    else {
      setMessage('privacy', STRINGS.SUCCESS_PRIVACY, 'success');
      setPrivacyTags([]);
    }
  };

  const handleDeleteEntries = async () => {
    if (deleteTags.length === 0) return;
    if (!window.confirm(STRINGS.CONFIRM_DELETE_ENTRIES)) return;
    clearMessages();
    const res = await deleteEntriesByTagsApi({ profileId: activeProfileId, tags: deleteTags, token });
    if (res.error) setMessage('deleteEntries', res.error, 'error');
    else {
      setMessage('deleteEntries', STRINGS.SUCCESS_DELETED_ENTRIES, 'success');
      setDeleteTags([]);
    }
  };

  const handleRenameTag = async () => {
    if (!renameTagOld || !renameTagNewName) return;
    clearMessages();
    const res = await updateTagApi({
      profileId: activeProfileId,
      tagId: renameTagOld.value,
      newName: renameTagNewName,
      token
    });
    if (res.error) setMessage('rename', res.error, 'error');
    else {
      setMessage('rename', STRINGS.SUCCESS_RENAME, 'success');
      setRenameTagOld(null);
      setRenameTagNewName('');
      await fetchTags();
    }
  };

  const handleDeleteTag = async () => {
    if (!deleteTag) return;
    if (!window.confirm(STRINGS.CONFIRM_DELETE_TAG)) return;
    clearMessages();
    const res = await deleteTagApi({ profileId: activeProfileId, tagId: deleteTag.value, token });
    if (res.error) setMessage('deleteTag', res.error, 'error');
    else {
      setMessage('deleteTag', STRINGS.SUCCESS_DELETE, 'success');
      setDeleteTag(null);
      await fetchTags();
    }
  };

  return (
    <ContentWrapper data-testid="tags-settings">
      <Block>
        <SettingsFlexbox>
          <SettingSectionTitle>
            <h4>{STRINGS.ADD_TAGS_IMPORTED_TITLE}</h4>
            <p>{STRINGS.ADD_TAGS_IMPORTED_DESC}</p>
          </SettingSectionTitle>
          <Settings>
            <TagsSelect
              value={addTagsImported}
              onChange={setAddTagsImported}
              searchAllTags={false}
              creatable={true}
              tagOptions={userTagsNames}
              placeholder={STRINGS.SELECT_PLACEHOLDER}
            />
            <ActionsRow $marginTop>
              <ActionButton onClick={handleApplyToImported} disabled={addTagsImported.length === 0}>
                {STRINGS.APPLY_IMPORTED}
              </ActionButton>
            </ActionsRow>
            <SectionMessage message={messages.imported} />
          </Settings>
        </SettingsFlexbox>
      </Block>

      <Block>
        <SettingsFlexbox>
          <SettingSectionTitle>
            <h4>{STRINGS.ADD_TAGS_ALL_TITLE}</h4>
            <p>{STRINGS.ADD_TAGS_ALL_DESC}</p>
          </SettingSectionTitle>
          <Settings>
            <TagsSelect
              value={addTagsAll}
              onChange={setAddTagsAll}
              searchAllTags={false}
              creatable={true}
              tagOptions={userTagsNames}
              placeholder={STRINGS.SELECT_PLACEHOLDER}
            />
            <ActionsRow $marginTop>
              <ActionButton onClick={handleApplyToAll} disabled={addTagsAll.length === 0}>
                {STRINGS.APPLY_ALL}
              </ActionButton>
            </ActionsRow>
            <SectionMessage message={messages.all} />
          </Settings>
        </SettingsFlexbox>
      </Block>

      <Block>
        <SettingsFlexbox>
          <SettingSectionTitle>
            <h4>{STRINGS.PRIVACY_TITLE}</h4>
            <p>{STRINGS.PRIVACY_DESC}</p>
          </SettingSectionTitle>
          <Settings>
            <TagsSelect
              value={privacyTags}
              onChange={setPrivacyTags}
              searchAllTags={false}
              tagOptions={userTagsNames}
              placeholder={STRINGS.SELECT_PLACEHOLDER}
            />
            <ActionsRow $marginTop $zIndex={10}>
              <SelectWrapper $width="300px">
                <Select
                  options={simplePrivacyOptions}
                  selectedOptionID={selectedPrivacy}
                  onChange={setSelectedPrivacy}
                  menuPlacement="auto"
                  isClearable={false}
                />
              </SelectWrapper>
              <ActionButton onClick={handleUpdatePrivacy} disabled={privacyTags.length === 0}>
                {STRINGS.UPDATE_PRIVACY}
              </ActionButton>
            </ActionsRow>
            <SectionMessage message={messages.privacy} />
          </Settings>
        </SettingsFlexbox>
      </Block>

      <Block>
        <SettingsFlexbox>
          <SettingSectionTitle>
            <h4>{STRINGS.DELETE_ENTRIES_TITLE}</h4>
            <WarningText>{STRINGS.DELETE_ENTRIES_DESC}</WarningText>
          </SettingSectionTitle>
          <Settings>
            <TagsSelect
              value={deleteTags}
              onChange={setDeleteTags}
              searchAllTags={false}
              tagOptions={userTagsNames}
              placeholder={STRINGS.SELECT_PLACEHOLDER}
            />
            <ActionsRow $marginTop>
              <ActionButton onClick={handleDeleteEntries} disabled={deleteTags.length === 0}>
                {STRINGS.DELETE_ENTRIES_BTN}
              </ActionButton>
            </ActionsRow>
            <SectionMessage message={messages.deleteEntries} />
          </Settings>
        </SettingsFlexbox>
      </Block>

      <Block>
        <SettingsFlexbox>
          <SettingSectionTitle>
            <h4>{STRINGS.RENAME_TITLE}</h4>
            <p>{STRINGS.RENAME_DESC}</p>
          </SettingSectionTitle>
          <Settings>
            <ActionsRow>
              <SelectWrapper $width="250px">
                <Select
                  options={userTags}
                  value={renameTagOld}
                  onChange={setRenameTagOld}
                  controlledValue
                  isClearable
                  placeholder={STRINGS.SELECT_SINGLE_PLACEHOLDER}
                />
              </SelectWrapper>
              <CenteredTextField
                label={STRINGS.RENAME_INPUT_LABEL}
                value={renameTagNewName}
                onChange={e => setRenameTagNewName(e.target.value)}
                touched
              />
              <ActionButton onClick={handleRenameTag} disabled={!renameTagOld || !renameTagNewName}>
                {STRINGS.RENAME_BTN}
              </ActionButton>
            </ActionsRow>
            <SectionMessage message={messages.rename} />
          </Settings>
        </SettingsFlexbox>
      </Block>

      <Block>
        <SettingsFlexbox>
          <SettingSectionTitle>
            <h4>{STRINGS.DELETE_TAG_TITLE}</h4>
            <p>{STRINGS.DELETE_TAG_DESC}</p>
          </SettingSectionTitle>
          <Settings>
            <ActionsRow>
              <SelectWrapper $width="250px">
                <Select
                  options={userTags}
                  value={deleteTag}
                  onChange={setDeleteTag}
                  controlledValue
                  isClearable
                  placeholder={STRINGS.SELECT_SINGLE_PLACEHOLDER}
                />
              </SelectWrapper>
              <ActionButton onClick={handleDeleteTag} disabled={!deleteTag}>
                {STRINGS.DELETE_TAG_BTN}
              </ActionButton>
            </ActionsRow>
            <SectionMessage message={messages.deleteTag} />
          </Settings>
        </SettingsFlexbox>
      </Block>
    </ContentWrapper>
  );
}
